import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { postService } from '@services/api/posts'
import { categoryService } from '@services/api/categories'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'
import { isInFreeTrial, getFreeTrialDaysLeft } from '@utils/freeTrial'

function CreateVideoPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [apiCategories, setApiCategories] = useState([])
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: '',
    description: '',
    videoFile: null,
    thumbnail: null,
    thumbnailPreview: null,
    price: '',
    condition: '',
    discount: ''
  })

  useEffect(() => {
    categoryService.getCategories()
      .then(res => {
        const list = res.data || res || []
        setApiCategories(Array.isArray(list) ? list : [])
      })
      .catch(() => setApiCategories([]))
  }, [])

  const handleVideoUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('video/')) {
      setFormData({ ...formData, videoFile: file })
    } else {
      toast.error('Veuillez sélectionner un fichier vidéo valide')
    }
  }

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('image/')) {
      setFormData({ 
        ...formData, 
        thumbnail: file,
        thumbnailPreview: URL.createObjectURL(file) 
      })
    } else {
      toast.error('Veuillez sélectionner une image valide')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.videoFile) {
      toast.error('Veuillez sélectionner une vidéo')
      return
    }

    if (!formData.title || !formData.category || !formData.location || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    const clientId = user?.client_id || user?.id
    if (!clientId) {
      toast.error('Vous devez être connecté pour publier une vidéo')
      return
    }

    setIsSubmitting(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('libele', formData.title)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('siteWeb', formData.location)
      formDataToSend.append('post_client_id', clientId)
      formDataToSend.append('post_categorie_id', formData.category)
      formDataToSend.append('type', '1') // 1 = vidéo
      if (formData.price) formDataToSend.append('prix', formData.price)

      // Vidéo comme fichier principal
      formDataToSend.append('files[]', formData.videoFile)
      // Miniature optionnelle
      if (formData.thumbnail) formDataToSend.append('files[]', formData.thumbnail)

      const response = await postService.createPost(formDataToSend)
      const postId = response.data?.post_id || response.data?.id

      if (isInFreeTrial(user)) {
        // Période d'essai : le backend auto-approuve pendant createPost
        const daysLeft = getFreeTrialDaysLeft(user)
        toast.success(`✅ Vidéo publiée ! Période d'essai — ${daysLeft} jour${daysLeft > 1 ? 's' : ''} restant${daysLeft > 1 ? 's' : ''}.`)
        setTimeout(() => navigate('/dashboard'), 1500)
      } else {
        // Hors période d'essai : rediriger vers le paiement
        navigate('/payment-video', {
          state: {
            postId: postId,
            initialDuration: 7,
            postData: {
              title: formData.title,
              category: formData.category,
              location: formData.location,
              description: formData.description
            }
          }
        })
      }
      
    } catch (error) {
      console.error('Erreur complète:', error)
      console.error('Erreur response:', error.response)
      console.error('Erreur data:', error.response?.data)
      
      const msg = error.response?.data?.message || error.message || 'Erreur lors de la publication'
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    navigate('/ligdi')
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  return (
    <div className="min-h-screen bg-[#fefcf8] overflow-x-hidden">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-2 sm:px-4 py-3 sm:py-8">
        <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-6 md:p-8 lg:p-12 overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#001F3F] mb-8">Publier une vidéo</h1>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Colonne gauche */}
              <div className="space-y-6">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Titre<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Titre de votre vidéo"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Catégorie<span className="text-red-600">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full max-w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                    required
                  >
                    <option value="">Sélectionnez une catégorie</option>
                    {apiCategories.filter(c => c.libele_categorie !== 'Toutes les catégories').map(c => (
                      <option key={c.categorie_id} value={c.categorie_id}>{c.libele_categorie}</option>
                    ))}
                  </select>
                </div>

                {/* Situation géographique */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Situation géographique<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Votre emplacement"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Description<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Décrivez votre vidéo"
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm resize-none"
                    required
                  ></textarea>
                </div>

                {/* Prix */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Prix (FCFA)
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="Ex: 50000"
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  />
                </div>

                {/* État */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    État
                  </label>
                  <select
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    className="w-full max-w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="">Sélectionnez l'état</option>
                    <option value="neuf">Neuf</option>
                    <option value="scelle">Scellé</option>
                    <option value="reconditionne">Reconditionné</option>
                    <option value="quasi_neuf">Quasi neuf</option>
                    <option value="excellent">Excellent</option>
                    <option value="bon">Bon état</option>
                    <option value="moyen">État moyen</option>
                    <option value="usage">Usagé</option>
                  </select>
                </div>

                {/* Réduction */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Réduction (%)
                  </label>
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleChange}
                    placeholder="Ex: 10"
                    min="0"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  />
                  {formData.discount > 0 && formData.price && (
                    <p className="text-xs text-gray-500 mt-1">
                      Prix après réduction: {Math.round(formData.price * (1 - formData.discount / 100)).toLocaleString()} FCFA
                    </p>
                  )}
                </div>
              </div>

              {/* Colonne droite */}
              <div className="space-y-6">
                {/* Upload vidéo */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Fichier vidéo<span className="text-red-600">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Formats acceptés: MP4, AVI, MOV, WebM. Taille maximale: 500 MB
                  </p>
                  
                  <label className="block w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#D4AF37] hover:bg-gray-50 transition-colors">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      required
                    />
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                    </svg>
                    {formData.videoFile ? (
                      <p className="text-sm text-[#D4AF37] font-semibold">{formData.videoFile.name}</p>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-[#001F3F] mb-1">Cliquez pour télécharger une vidéo</p>
                        <p className="text-xs text-gray-500">ou glissez-déposez votre fichier ici</p>
                      </>
                    )}
                  </label>
                </div>

                {/* Miniature */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Miniature (optionnel)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Image de couverture pour votre vidéo
                  </p>
                  
                  <label className="block w-full border-2 border-dashed border-gray-300 rounded-lg overflow-hidden cursor-pointer hover:border-[#D4AF37] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      className="hidden"
                    />
                    {formData.thumbnailPreview ? (
                      <div className="relative aspect-video">
                        <img src={formData.thumbnailPreview} alt="Miniature" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <p className="text-white text-sm font-semibold">Changer la miniature</p>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video flex flex-col items-center justify-center p-6">
                        <svg className="w-12 h-12 text-gray-400 mb-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                        </svg>
                        <p className="text-xs text-gray-500 text-center">Ajouter une miniature</p>
                      </div>
                    )}
                  </label>
                </div>

                {/* Récapitulatif de la publication */}
                <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#FFD700]/5 border-2 border-[#D4AF37]/30 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#001F3F] mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    Durée de publication
                  </h3>
                  <div className="bg-white/80 rounded-lg p-3 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">Durée d'affichage :</span>
                      <span className="text-lg font-bold text-[#D4AF37]">
                        À définir au paiement
                      </span>
                    </div>
                  </div>
                  <ul className="text-xs text-gray-700 space-y-1">
                    <li>✓ Affichage sur la page d'accueil</li>
                    <li>✓ Visible par tous les utilisateurs</li>
                    <li>✓ Lecture automatique optimisée</li>
                    <li>✓ Statistiques de visionnage</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bouton Publier */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-20 py-3 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#c09d2a] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Publication en cours...
                  </span>
                ) : (
                  'Publier'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  )
}

export default CreateVideoPage
